import { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

export type OffenseType = 
  | 'speeding' | 'parking' | 'red_light' | 'stop_sign' 
  | 'phone_use' | 'seatbelt' | 'bus_lane' | 'pedestrian_crossing'
  | 'zfe_violation' | 'toll_evasion' | 'fps' | 'other';

export type FineStatus = 
  | 'received' | 'pending_designation' | 'designated' 
  | 'contested' | 'paid' | 'cancelled';

export interface TrafficFine {
  id: string;
  user_id: string;
  organization_id?: string | null;
  vehicle_id?: string | null;
  driver_id?: string | null;
  document_id?: string | null;
  reference_number?: string | null;
  license_plate: string;
  offense_date?: string | null;
  offense_location?: string | null;
  offense_type: OffenseType;
  offense_description?: string | null;
  speed_recorded?: number | null;
  speed_limit?: number | null;
  speed_retained?: number | null;
  fine_amount?: number | null;
  reduced_amount?: number | null;
  increased_amount?: number | null;
  points_withdrawn?: number | null;
  fine_class?: number | null;
  payment_deadline?: string | null;
  reduced_deadline?: string | null;
  designation_deadline?: string | null;
  status: FineStatus;
  payment_date?: string | null;
  payment_reference?: string | null;
  extracted_data?: Record<string, unknown> | null;
  confidence_score?: number | null;
  notes?: string | null;
  created_at: string;
  updated_at: string;
  // Joined data
  vehicle?: { license_plate: string; brand: string; model: string } | null;
  driver?: { first_name: string; last_name: string; email: string } | null;
}

export interface FineAction {
  id: string;
  fine_id: string;
  action_type: string;
  actor_type: string;
  actor_id?: string | null;
  notes?: string | null;
  metadata?: Record<string, unknown> | null;
  created_at: string;
}

export interface CreateFineInput {
  license_plate: string;
  offense_type?: OffenseType;
  offense_date?: string | null;
  offense_location?: string | null;
  offense_description?: string | null;
  reference_number?: string | null;
  fine_amount?: number | null;
  reduced_amount?: number | null;
  increased_amount?: number | null;
  points_withdrawn?: number | null;
  fine_class?: number | null;
  payment_deadline?: string | null;
  reduced_deadline?: string | null;
  designation_deadline?: string | null;
  speed_recorded?: number | null;
  speed_limit?: number | null;
  speed_retained?: number | null;
  vehicle_id?: string | null;
  driver_id?: string | null;
  document_id?: string | null;
  extracted_data?: Record<string, unknown> | null;
  confidence_score?: number | null;
  notes?: string | null;
}

export function useTrafficFines() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const finesQuery = useQuery({
    queryKey: ['traffic-fines', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      const { data, error } = await supabase
        .from('traffic_fines')
        .select(`
          *,
          vehicle:vehicles(license_plate, brand, model),
          driver:drivers(first_name, last_name, email)
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as TrafficFine[];
    },
    enabled: !!user?.id,
  });

  const createFine = useMutation({
    mutationFn: async (input: CreateFineInput) => {
      if (!user?.id) throw new Error('Non authentifié');

      // Auto-link vehicle by license plate if not provided
      let vehicleId = input.vehicle_id;
      if (!vehicleId && input.license_plate) {
        const { data: vehicle } = await supabase
          .from('vehicles')
          .select('id')
          .eq('user_id', user.id)
          .ilike('license_plate', input.license_plate.replace(/[-\s]/g, ''))
          .maybeSingle();
        
        if (vehicle) {
          vehicleId = vehicle.id;
        }
      }

      const { data, error } = await supabase
        .from('traffic_fines')
        .insert({
          user_id: user.id,
          license_plate: input.license_plate,
          offense_type: input.offense_type || 'other',
          offense_date: input.offense_date,
          offense_location: input.offense_location,
          offense_description: input.offense_description,
          reference_number: input.reference_number,
          fine_amount: input.fine_amount,
          reduced_amount: input.reduced_amount,
          increased_amount: input.increased_amount,
          points_withdrawn: input.points_withdrawn,
          fine_class: input.fine_class,
          payment_deadline: input.payment_deadline,
          reduced_deadline: input.reduced_deadline,
          designation_deadline: input.designation_deadline,
          speed_recorded: input.speed_recorded,
          speed_limit: input.speed_limit,
          speed_retained: input.speed_retained,
          vehicle_id: vehicleId,
          driver_id: input.driver_id,
          document_id: input.document_id,
          extracted_data: input.extracted_data,
          confidence_score: input.confidence_score,
          notes: input.notes,
        } as any)
        .select()
        .single();

      if (error) throw error;

      // Create initial action
      await supabase.from('fine_actions').insert({
        fine_id: data.id,
        action_type: 'created',
        actor_type: 'user',
        actor_id: user.id,
        notes: 'Amende ajoutée manuellement',
      });

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['traffic-fines'] });
      toast.success('Amende enregistrée');
    },
    onError: (error) => {
      console.error('Error creating fine:', error);
      toast.error('Erreur lors de l\'enregistrement');
    },
  });

  const updateFine = useMutation({
    mutationFn: async ({ id, status, driver_id, payment_date, payment_reference, notes }: Partial<TrafficFine> & { id: string }) => {
      const { data, error } = await supabase
        .from('traffic_fines')
        .update({ status, driver_id, payment_date, payment_reference, notes })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['traffic-fines'] });
    },
  });

  const designateDriver = useMutation({
    mutationFn: async ({ fineId, driverId }: { fineId: string; driverId: string }) => {
      if (!user?.id) throw new Error('Non authentifié');

      const { error: updateError } = await supabase
        .from('traffic_fines')
        .update({
          driver_id: driverId,
          status: 'designated',
        })
        .eq('id', fineId);

      if (updateError) throw updateError;

      // Get driver info for the action note
      const { data: driver } = await supabase
        .from('drivers')
        .select('first_name, last_name')
        .eq('id', driverId)
        .single();

      await supabase.from('fine_actions').insert({
        fine_id: fineId,
        action_type: 'designated',
        actor_type: 'user',
        actor_id: user.id,
        notes: driver ? `Conducteur désigné : ${driver.first_name} ${driver.last_name}` : 'Conducteur désigné',
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['traffic-fines'] });
      toast.success('Conducteur désigné');
    },
    onError: (error) => {
      console.error('Error designating driver:', error);
      toast.error('Erreur lors de la désignation');
    },
  });

  const changeDriver = useMutation({
    mutationFn: async ({ fineId, driverId, previousDriverName }: { fineId: string; driverId: string; previousDriverName?: string }) => {
      if (!user?.id) throw new Error('Non authentifié');

      const { error: updateError } = await supabase
        .from('traffic_fines')
        .update({
          driver_id: driverId,
        })
        .eq('id', fineId);

      if (updateError) throw updateError;

      // Get new driver info for the action note
      const { data: driver } = await supabase
        .from('drivers')
        .select('first_name, last_name')
        .eq('id', driverId)
        .single();

      const newDriverName = driver ? `${driver.first_name} ${driver.last_name}` : 'Nouveau conducteur';
      const noteText = previousDriverName 
        ? `Conducteur modifié : ${previousDriverName} → ${newDriverName}`
        : `Conducteur modifié : ${newDriverName}`;

      await supabase.from('fine_actions').insert({
        fine_id: fineId,
        action_type: 'designation_changed',
        actor_type: 'user',
        actor_id: user.id,
        notes: noteText,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['traffic-fines'] });
      queryClient.invalidateQueries({ queryKey: ['fine-actions'] });
      toast.success('Conducteur modifié');
    },
    onError: (error) => {
      console.error('Error changing driver:', error);
      toast.error('Erreur lors de la modification');
    },
  });

  const markAsPaid = useMutation({
    mutationFn: async ({ fineId, paymentReference }: { fineId: string; paymentReference?: string }) => {
      if (!user?.id) throw new Error('Non authentifié');

      const { error: updateError } = await supabase
        .from('traffic_fines')
        .update({
          status: 'paid',
          payment_date: new Date().toISOString().split('T')[0],
          payment_reference: paymentReference,
        })
        .eq('id', fineId);

      if (updateError) throw updateError;

      await supabase.from('fine_actions').insert({
        fine_id: fineId,
        action_type: 'paid',
        actor_type: 'user',
        actor_id: user.id,
        notes: paymentReference ? `Paiement effectué (réf: ${paymentReference})` : 'Paiement effectué',
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['traffic-fines'] });
      toast.success('Amende marquée comme payée');
    },
    onError: (error) => {
      console.error('Error marking as paid:', error);
      toast.error('Erreur lors du marquage');
    },
  });

  const contestFine = useMutation({
    mutationFn: async ({ fineId, reason }: { fineId: string; reason: string }) => {
      if (!user?.id) throw new Error('Non authentifié');

      const { error: updateError } = await supabase
        .from('traffic_fines')
        .update({ status: 'contested' })
        .eq('id', fineId);

      if (updateError) throw updateError;

      await supabase.from('fine_actions').insert({
        fine_id: fineId,
        action_type: 'contested',
        actor_type: 'user',
        actor_id: user.id,
        notes: `Contestation : ${reason}`,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['traffic-fines'] });
      toast.success('Contestation enregistrée');
    },
  });

  const deleteFine = useMutation({
    mutationFn: async (fineId: string) => {
      const { error } = await supabase
        .from('traffic_fines')
        .delete()
        .eq('id', fineId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['traffic-fines'] });
      toast.success('Amende supprimée');
    },
  });

  const getFineActions = useCallback(async (fineId: string): Promise<FineAction[]> => {
    const { data, error } = await supabase
      .from('fine_actions')
      .select('*')
      .eq('fine_id', fineId)
      .order('created_at', { ascending: true });

    if (error) throw error;
    return data as FineAction[];
  }, []);

  // Statistics
  const stats = {
    total: finesQuery.data?.length || 0,
    pending: finesQuery.data?.filter(f => f.status === 'received' || f.status === 'pending_designation').length || 0,
    pendingDesignation: finesQuery.data?.filter(f => 
      (f.status === 'received' || f.status === 'pending_designation') && 
      f.designation_deadline
    ).length || 0,
    paid: finesQuery.data?.filter(f => f.status === 'paid').length || 0,
    totalAmount: finesQuery.data?.reduce((sum, f) => sum + (f.fine_amount || 0), 0) || 0,
    totalPendingAmount: finesQuery.data
      ?.filter(f => f.status !== 'paid' && f.status !== 'cancelled')
      .reduce((sum, f) => sum + (f.fine_amount || 0), 0) || 0,
  };

  return {
    fines: finesQuery.data || [],
    isLoading: finesQuery.isLoading,
    error: finesQuery.error,
    stats,
    createFine,
    updateFine,
    designateDriver,
    changeDriver,
    markAsPaid,
    contestFine,
    deleteFine,
    getFineActions,
    refetch: finesQuery.refetch,
  };
}

// Helper to get days remaining until deadline
export function getDaysRemaining(deadline: string | null | undefined): number | null {
  if (!deadline) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const deadlineDate = new Date(deadline);
  deadlineDate.setHours(0, 0, 0, 0);
  const diffTime = deadlineDate.getTime() - today.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

// Helper to get current applicable amount
export function getCurrentAmount(fine: TrafficFine): number {
  const today = new Date();
  
  if (fine.reduced_deadline && new Date(fine.reduced_deadline) >= today && fine.reduced_amount) {
    return fine.reduced_amount;
  }
  
  if (fine.payment_deadline && new Date(fine.payment_deadline) >= today && fine.fine_amount) {
    return fine.fine_amount;
  }
  
  if (fine.increased_amount) {
    return fine.increased_amount;
  }
  
  return fine.fine_amount || 0;
}

// Offense type labels
export const OFFENSE_TYPE_LABELS: Record<OffenseType, string> = {
  speeding: 'Excès de vitesse',
  parking: 'Stationnement',
  red_light: 'Feu rouge',
  stop_sign: 'Stop',
  phone_use: 'Téléphone au volant',
  seatbelt: 'Ceinture de sécurité',
  bus_lane: 'Voie de bus',
  pedestrian_crossing: 'Passage piéton',
  zfe_violation: 'Zone à faibles émissions',
  toll_evasion: 'Péage',
  fps: 'Forfait post-stationnement',
  other: 'Autre',
};

// Status labels
export const FINE_STATUS_LABELS: Record<FineStatus, string> = {
  received: 'Reçue',
  pending_designation: 'En attente de désignation',
  designated: 'Conducteur désigné',
  contested: 'Contestée',
  paid: 'Payée',
  cancelled: 'Annulée',
};
